import { Close, Edit, PersonAddAlt1 } from "@mui/icons-material";
import {
  Autocomplete,
  IconButton,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import Avatar from "common/components/Avatar";
import Typography from "common/components/Typography";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Player } from "types/Player/type";
import { Player as PlayerBox } from "service/player/type";

type SelectPlayerType = {
  player?: Player;
  playersList: PlayerBox[];
  score?: number;
  color: "player1" | "player2";
  onClick: () => void;
  onClickChangePlayer: (player: PlayerBox) => void;
  onSearchChange?: (search: string) => void;
  // Solo : le joueur peut prendre la box d'un autre joueur sur ce siège.
  editable?: boolean;
  // Solo : le siège vide se remplit avec un joueur simulé au lieu d'accueillir le joueur.
  simulateOnEmpty?: boolean;
  // Solo : siège de coéquipier, fermé tant que le joueur n'a pas choisi son camp.
  joinDisabled?: boolean;
};

const SelectPlayer = (props: SelectPlayerType) => {
  const { t } = useTranslation();
  const {
    player,
    playersList,
    color,
    score,
    onClick,
    onClickChangePlayer,
    onSearchChange,
    editable = false,
    simulateOnEmpty = false,
    joinDisabled = false,
  } = props;
  const [edit, setEdit] = useState(false);
  const [inputValue, setInputValue] = useState("");

  // Choisir un joueur dans la liste vaut validation : pas de bouton de confirmation.
  const pickPlayer = (newPlayer: PlayerBox | null) => {
    if (!newPlayer) return;
    onClickChangePlayer(newPlayer);
    setEdit(false);
    setInputValue("");
  };

  const playerPicker = (
    <Autocomplete
      value={null}
      onChange={(_, newPlayer: PlayerBox | null) => pickPlayer(newPlayer)}
      inputValue={inputValue}
      onInputChange={(_, value) => {
        setInputValue(value);
        if (value.trim() !== "") {
          onSearchChange?.(value);
        }
      }}
      onClose={() => {
        if (inputValue.trim() === "") {
          onSearchChange?.("");
        }
      }}
      getOptionLabel={(playerBox) => playerBox.pseudo ?? ""}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      options={playersList}
      sx={{ width: "13rem" }}
      renderOption={(props, option) => (
        <MenuItem {...props} key={option.id}>
          {option.pseudo}
        </MenuItem>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t("Sélectionne un joueur")}
          InputLabelProps={{
            ...params.InputLabelProps,
            children: undefined,
          }}
        />
      )}
    />
  );

  return (
    <Stack
      alignItems="center"
      direction={color === "player1" ? "row" : "row-reverse"}
      width="100%"
      gap={2}
      sx={{
        background: player
          ? ({ palette }) =>
              `linear-gradient(to ${color === "player1" ? "right" : "left"}, ${
                palette.common[color].text
              } 1%, ${palette.common[color].bgConstel} 1% 75%, transparent)`
          : "transparent",
        height: "fit-content",
        padding: 2,
      }}
    >
      <Avatar character={player?.avatar} />
      {player ? (
        <Stack direction="column" width="100%" minWidth={0}>
          <Stack direction="row" justifyContent="center" alignItems="center">
            {edit ? (
              playerPicker
            ) : (
              <Typography
                textAlign="center"
                textOverflow="ellipsis"
                overflow="hidden"
                whiteSpace="nowrap"
              >
                {player?.pseudo ?? ""}
              </Typography>
            )}
            {editable && (
              <IconButton
                onClick={() => {
                  setEdit(!edit);
                  setInputValue("");
                }}
              >
                {edit ? <Close /> : <Edit />}
              </IconButton>
            )}
          </Stack>
          <Typography
            size="sm"
            textAlign="center"
            textOverflow="ellipsis"
            overflow="hidden"
            whiteSpace="nowrap"
            width="100%"
          >
            {t("Score de box : {{score}}", {
              score: score ?? "...",
            })}
          </Typography>
        </Stack>
      ) : simulateOnEmpty ? (
        playerPicker
      ) : (
        <>
          <Typography size="sm" color="gray">
            {joinDisabled
              ? t("Choisis un côté")
              : t("En attente de joueurs...")}
          </Typography>
          <IconButton
            sx={{ color: ({ palette }) => palette.common[color].text }}
            onClick={onClick}
            disabled={joinDisabled}
          >
            <PersonAddAlt1 />
          </IconButton>
        </>
      )}
    </Stack>
  );
};

export default SelectPlayer;
